/**
 * Bottom-dock QC list derived from the open case and its results summary.
 */

import { useEffect, useState } from "react";
import { EmptyState } from "@/components/ui/primitives";
import { getResultsSummary } from "@/ipc/api";
import type { ResultsSummary } from "@/ipc/contract";
import { deriveQcReport, type CheckStatus } from "@/viewer/qc";
import { useCurrentCase } from "@/store/appStore";
import { AlertTriangle, CheckCircle2, CircleDashed, MinusCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export function QcPanel() {
  const manifest = useCurrentCase();
  const [results, setResults] = useState<ResultsSummary | null>(null);

  useEffect(() => {
    if (!manifest) {
      setResults(null);
      return;
    }
    let cancelled = false;
    void getResultsSummary(manifest.caseId)
      .then((summary) => !cancelled && setResults(summary))
      .catch(() => !cancelled && setResults(null));
    return () => {
      cancelled = true;
    };
  }, [manifest]);

  if (!manifest) {
    return <EmptyState title="No case open" hint="QC checks appear after a case has outputs." />;
  }

  const report = deriveQcReport(manifest, results);

  return (
    <div className="h-full overflow-auto px-3 py-2 text-xs">
      <p className="mb-2 text-neutral-600">{report.summary}</p>
      {report.checks.map((check) => (
        <div key={check.id} className="flex items-start gap-2 py-1">
          <StatusIcon status={check.status} />
          <div>
            <div className="font-medium text-neutral-800">{check.label}</div>
            <div className="text-[11px] text-neutral-500">{check.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusIcon({ status }: { status: CheckStatus }) {
  const cls = "h-3.5 w-3.5 shrink-0 mt-0.5";
  if (status === "pass") return <CheckCircle2 className={cn(cls, "text-green-600")} />;
  if (status === "warning") return <AlertTriangle className={cn(cls, "text-amber-500")} />;
  if (status === "unavailable") return <MinusCircle className={cn(cls, "text-neutral-400")} />;
  return <CircleDashed className={cn(cls, "text-neutral-400")} />;
}
